// Espejo de PERMISSIONS y DEFAULT_ROLE_PERMISSIONS en
// zc_backend/middleware/permissions.js. Se mantiene a mano: si allá se agrega un
// permiso o un rol, hay que agregarlo acá también.
export const PERMISSIONS = {
  DOCUMENTS_RESTRICTED_VIEW: 'documents.restricted.view',
  DOCUMENTS_RESTRICTED_DOWNLOAD: 'documents.restricted.download',
  DOCUMENTS_DOWNLOAD: 'documents.download',
  DOCUMENTS_DELETE: 'documents.delete',
  DOCUMENTS_EDIT: 'documents.edit',
  DOCUMENTS_GLOBAL_EXPIRY_READ: 'documents.global_expiry.read',
  // F30 / F30-1 de la empresa (antecedentes laborales y previsionales). Permiso
  // aparte de documents.restricted.view (ese cubre los documentos de una persona). Por
  // defecto solo RRHH y super admin tienen uno y otro — ver DOCUMENTOS_SOLO_RRHH.
  DOCUMENTS_F30_VIEW: 'documents.f30.view',
  ACCREDITATION_MANAGE: 'accreditation.manage',
  // Resolver (aprobar/rechazar) solicitudes de documentos de empresa. Aparte de
  // accreditation.manage a propósito: ese lo tiene también 'rrhh', y acá resuelven solo
  // admin y supervisor. Solicitar no requiere permiso, basta con estar autenticado.
  EMPRESA_DOCUMENTS_REVIEW: 'empresa_documents.review',
  AUDIT_READ: 'audit.read',
  AUDIT_MANAGE: 'audit.manage',
  USERS_MANAGE: 'users.manage',
  CLIENTS_VISIBILITY_MANAGE: 'clients.visibility.manage',
  PERSONAL_ROLES_MANAGE: 'personal_roles.manage',
  CACHE_REFRESH: 'cache.refresh',
  PROGRAMACION_ACCESS: 'programacion.access',
  PRESISI_UPLOAD: 'presisi.upload',
  PRESISI_REVIEW: 'presisi.review',
  PRESISI_VIEW_ALL: 'presisi.view_all',
  NOTIFICATIONS_MANAGE: 'notifications.manage',
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

const allPermissions = Object.values(PERMISSIONS);

// Documentos confidenciales de RRHH (contrato, F30, cotizaciones, liquidación, sueldo y el
// F30/F30-1 de la empresa): solo super admin ('admin') y RRHH ('rrhh').
// Espejo de DOCUMENTOS_SOLO_RRHH y migrations/064 del backend.
const DOCUMENTOS_SOLO_RRHH: Permission[] = [
  PERMISSIONS.DOCUMENTS_RESTRICTED_VIEW,
  PERMISSIONS.DOCUMENTS_RESTRICTED_DOWNLOAD,
  PERMISSIONS.DOCUMENTS_F30_VIEW,
];

// Base operativa: subir por Pre-SISI y descargar.
const PERMISOS_PRE_SISI_BASICO: Permission[] = [
  PERMISSIONS.PRESISI_UPLOAD,
  PERMISSIONS.DOCUMENTS_DOWNLOAD,
];

// Roles vigentes desde migrations/064 del backend. Se eliminaron supervisor, usuario,
// user, operador y los tres de Pre-SISI (básico, medio, alto): un rol que no esté acá
// resuelve a cero permisos.
const fallbackByRole: Record<string, Permission[]> = {
  // Super admin: todo; único que administra usuarios y roles.
  admin: allPermissions,
  // Admin: todo salvo los documentos de RRHH, administrar usuarios y roles, y la revisión
  // de Pre-SISI (solo super admin desde migrations/071 del backend).
  administrador: allPermissions.filter(p =>
    !DOCUMENTOS_SOLO_RRHH.includes(p) && p !== PERMISSIONS.USERS_MANAGE
    && p !== PERMISSIONS.PRESISI_REVIEW),
  // RRHH: el set que tenía 'supervisor', el rol de la cuenta de RRHH hasta la 064, salvo
  // la revisión de Pre-SISI (071): ve el buzón completo con presisi.view_all.
  rrhh: [
    ...DOCUMENTOS_SOLO_RRHH,
    PERMISSIONS.DOCUMENTS_DOWNLOAD,
    PERMISSIONS.DOCUMENTS_DELETE,
    PERMISSIONS.DOCUMENTS_EDIT,
    PERMISSIONS.DOCUMENTS_GLOBAL_EXPIRY_READ,
    PERMISSIONS.ACCREDITATION_MANAGE,
    PERMISSIONS.EMPRESA_DOCUMENTS_REVIEW,
    PERMISSIONS.AUDIT_READ,
    PERMISSIONS.AUDIT_MANAGE,
    PERMISSIONS.PERSONAL_ROLES_MANAGE,
    PERMISSIONS.PROGRAMACION_ACCESS,
    PERMISSIONS.PRESISI_UPLOAD,
    PERMISSIONS.PRESISI_VIEW_ALL,
  ],
  planificacion: [...PERMISOS_PRE_SISI_BASICO, PERMISSIONS.PROGRAMACION_ACCESS],
  // Pre-SISI: solo entra a Pre-SISI; ve todo el buzón, sube y descarga. A nivel usuario:
  // no aprueba ni corrige datos, y no ve documentos de RRHH.
  presisi: [...PERMISOS_PRE_SISI_BASICO, PERMISSIONS.PRESISI_VIEW_ALL],
  // Destinatario de los avisos de vencimiento del área 'Prevención'
  // (zc_backend/services/vencimientosService.js, migrations/048). Sin usuarios hoy.
  prevencion: [
    ...PERMISOS_PRE_SISI_BASICO,
    PERMISSIONS.DOCUMENTS_GLOBAL_EXPIRY_READ,
    PERMISSIONS.DOCUMENTS_EDIT,
  ],
};

// Roles conocidos por el frontend (claves de fallbackByRole) — usado por el panel de envío
// manual de notificaciones para armar el selector de "destinatarios por rol". No es un
// catálogo cerrado del lado backend (resolverUsuariosPorRol acepta cualquier string), pero
// mandar a un rol que no existe acá simplemente no encuentra usuarios.
export const ROLES_CONOCIDOS = Object.keys(fallbackByRole);

// Etiqueta legible para un rol en UI — el panel de notificaciones manuales
// (NotificacionesAdminPage.tsx) lista ROLES_CONOCIDOS como botones de destinatario, y
// claves como 'administrador' o 'presisi' no se explican solas. Un rol sin entrada acá
// (agregado a fallbackByRole pero no aquí) simplemente se muestra tal cual.
export const ETIQUETA_ROL: Record<string, string> = {
  admin: 'Super admin',
  administrador: 'Admin',
  rrhh: 'RRHH',
  planificacion: 'Planificación',
  presisi: 'Pre-SISI',
  prevencion: 'Prevención de Riesgos',
};

export const etiquetaRol = (rol: string): string => ETIQUETA_ROL[rol] || rol;

// Resumen de una línea por rol para el listado de usuarios (UsuariosPage.tsx). El
// detalle de qué puede y qué no se deriva de fallbackByRole con DESCRIPCION_PERMISO,
// así que esto es solo la frase de contexto: si cambian los permisos de un rol,
// revisar que la frase siga siendo cierta.
export const DESCRIPCION_ROL: Record<string, string> = {
  admin: 'Acceso total, incluidos los documentos de RRHH. Único que administra usuarios y roles.',
  administrador: 'Todo, salvo los documentos de RRHH y administrar usuarios y roles.',
  rrhh: 'Documentos de RRHH, acreditaciones, auditoría y revisión Pre-SISI. No administra usuarios.',
  planificacion: 'Sube y descarga documentos, más el módulo de Programación.',
  presisi: 'Solo Pre-SISI: ve a todo el personal, sube y descarga. No aprueba ni ve documentos de RRHH.',
  prevencion: 'Recibe avisos de vencimientos y corrige datos de documentos, pero no los elimina.',
};

// Texto corto por permiso, para listar en UI qué puede y qué no puede hacer un rol.
export const DESCRIPCION_PERMISO: Record<Permission, string> = {
  'documents.restricted.view': 'Ver documentos restringidos',
  'documents.restricted.download': 'Descargar documentos restringidos',
  'documents.download': 'Descargar documentos',
  'documents.delete': 'Eliminar documentos',
  'documents.edit': 'Corregir datos de documentos',
  'documents.global_expiry.read': 'Ver vencimientos globales',
  'documents.f30.view': 'Ver F30 / F30-1 de la empresa',
  'accreditation.manage': 'Gestionar acreditaciones',
  'empresa_documents.review': 'Aprobar documentos de empresa',
  'audit.read': 'Ver auditoría',
  'audit.manage': 'Gestionar auditoría',
  'users.manage': 'Administrar usuarios',
  'clients.visibility.manage': 'Configurar visibilidad de clientes',
  'personal_roles.manage': 'Asignar roles al personal',
  'cache.refresh': 'Refrescar caché',
  'programacion.access': 'Módulo Programación',
  'presisi.upload': 'Subir documentos (Pre-SISI)',
  'presisi.review': 'Revisar documentos (Pre-SISI)',
  'presisi.view_all': 'Ver todo el personal (Pre-SISI)',
  'notifications.manage': 'Enviar notificaciones',
};

export const TODOS_LOS_PERMISOS: Permission[] = allPermissions;

export const normalizeRole = (role?: string | null) => String(role || '').trim().toLowerCase();

// ── Acceso por app y pestañas ───────────────────────────────────────────────────────
// Espejo de zc_backend/config/catalogoPermisos.js, que es el que manda: los roles se
// editan desde /configuracion/roles y el backend entrega el set resuelto en
// GET /auth/permissions. Acá se necesita la lista igual, porque las RUTAS son del
// frontend (sidebar, guardas, a dónde ir después del login) y para el respaldo de abajo.
export type App = 'app.sisi' | 'app.presisi';

export const APP = { SISI: 'app.sisi', PRESISI: 'app.presisi' } as const;

// Claves de pestaña como constantes, para no escribir strings sueltos en las guardas.
export const TAB = {
  DASHBOARD: 'tab.dashboard',
  PERSONAL_LISTADO: 'tab.personal.listado',
  PERSONAL_ESTADO_DOCUMENTACION: 'tab.personal.estado_documentacion',
  PERSONAL_CARGA_MASIVA: 'tab.personal.carga_masiva',
  DOCUMENTOS_EXISTENTES: 'tab.documentos.existentes',
  SERVICIOS_COBERTURA: 'tab.servicios.cobertura',
  SERVICIOS_VISUALIZACION: 'tab.servicios.visualizacion',
  SERVICIOS_SERVICIOS: 'tab.servicios.servicios',
  SERVICIOS_ACREDITACION: 'tab.servicios.acreditacion',
  PROGRAMACION_PLANIFICACION: 'tab.programacion.planificacion',
  PROGRAMACION_RESUMEN: 'tab.programacion.resumen',
  CONFIG_USUARIOS: 'tab.config.usuarios',
  CONFIG_ROLES: 'tab.config.roles',
  CONFIG_AUDITORIA: 'tab.config.auditoria',
  CONFIG_CENTROS_COSTO: 'tab.config.centros_costo',
  CONFIG_NOTIFICACIONES: 'tab.config.notificaciones',
  CONFIG_RESPONSABLES: 'tab.config.responsables',
  PRESISI_CARGA: 'tab.presisi.carga',
  PRESISI_DOCUMENTOS_EMPRESA: 'tab.presisi.documentos_empresa',
  PRESISI_MIS_SOLICITUDES: 'tab.presisi.mis_solicitudes',
  PRESISI_RESPONSABILIDADES: 'tab.presisi.responsabilidades',
  PRESISI_REVISION: 'tab.presisi.revision',
} as const;

export type ClavePestana = typeof TAB[keyof typeof TAB];
export type Clave = Permission | App | ClavePestana;

export interface Pestana {
  key: ClavePestana;
  app: App;
  label: string;
  ruta: string;
  requiere?: Permission[];
}

// En el orden del sidebar: primeraPestanaPermitida elige la primera de esta lista.
export const PESTANAS: Pestana[] = [
  { key: TAB.DASHBOARD, app: 'app.sisi', label: 'Dashboard', ruta: '/dashboard' },
  { key: TAB.PERSONAL_LISTADO, app: 'app.sisi', label: 'Listado de personal', ruta: '/personal' },
  { key: TAB.PERSONAL_ESTADO_DOCUMENTACION, app: 'app.sisi', label: 'Estado de documentación', ruta: '/personal/estado-documentacion' },
  { key: TAB.PERSONAL_CARGA_MASIVA, app: 'app.sisi', label: 'Carga masiva de documentos', ruta: '/personal/carga-masiva' },
  { key: TAB.DOCUMENTOS_EXISTENTES, app: 'app.sisi', label: 'Documentos existentes', ruta: '/documentos/existentes' },
  { key: TAB.SERVICIOS_COBERTURA, app: 'app.sisi', label: 'Cobertura', ruta: '/servicios/cobertura' },
  { key: TAB.SERVICIOS_VISUALIZACION, app: 'app.sisi', label: 'Visualización', ruta: '/servicios/visualizacion' },
  { key: TAB.SERVICIOS_SERVICIOS, app: 'app.sisi', label: 'Servicios', ruta: '/servicios' },
  { key: TAB.SERVICIOS_ACREDITACION, app: 'app.sisi', label: 'Acreditación', ruta: '/acreditacion', requiere: [PERMISSIONS.ACCREDITATION_MANAGE] },
  { key: TAB.PROGRAMACION_PLANIFICACION, app: 'app.sisi', label: 'Planificación', ruta: '/planificacion', requiere: [PERMISSIONS.PROGRAMACION_ACCESS] },
  { key: TAB.PROGRAMACION_RESUMEN, app: 'app.sisi', label: 'Resumen', ruta: '/planificacion/resumen', requiere: [PERMISSIONS.PROGRAMACION_ACCESS] },
  { key: TAB.CONFIG_USUARIOS, app: 'app.sisi', label: 'Usuarios', ruta: '/configuracion/usuarios', requiere: [PERMISSIONS.USERS_MANAGE] },
  { key: TAB.CONFIG_ROLES, app: 'app.sisi', label: 'Roles y permisos', ruta: '/configuracion/roles', requiere: [PERMISSIONS.USERS_MANAGE] },
  { key: TAB.CONFIG_AUDITORIA, app: 'app.sisi', label: 'Auditoría', ruta: '/configuracion/auditoria', requiere: [PERMISSIONS.AUDIT_READ] },
  { key: TAB.CONFIG_CENTROS_COSTO, app: 'app.sisi', label: 'Centros de costo', ruta: '/configuracion/centros-costo' },
  { key: TAB.CONFIG_NOTIFICACIONES, app: 'app.sisi', label: 'Notificaciones', ruta: '/configuracion/notificaciones', requiere: [PERMISSIONS.NOTIFICATIONS_MANAGE] },
  { key: TAB.CONFIG_RESPONSABLES, app: 'app.sisi', label: 'Responsables de documentos', ruta: '/configuracion/responsables', requiere: [PERMISSIONS.ACCREDITATION_MANAGE] },
  { key: TAB.PRESISI_CARGA, app: 'app.presisi', label: 'Cargar documentos', ruta: '/pre-sisi', requiere: [PERMISSIONS.PRESISI_UPLOAD] },
  { key: TAB.PRESISI_DOCUMENTOS_EMPRESA, app: 'app.presisi', label: 'Documentos de empresa', ruta: '/pre-sisi/documentos-empresa' },
  { key: TAB.PRESISI_MIS_SOLICITUDES, app: 'app.presisi', label: 'Mis solicitudes', ruta: '/pre-sisi/mis-solicitudes' },
  { key: TAB.PRESISI_RESPONSABILIDADES, app: 'app.presisi', label: 'Mis responsabilidades', ruta: '/pre-sisi/responsabilidades' },
  { key: TAB.PRESISI_REVISION, app: 'app.presisi', label: 'Revisión', ruta: '/pre-sisi/admin', requiere: [PERMISSIONS.PRESISI_REVIEW] },
];

// Respaldo para cuando el backend no entrega acceso/pestañas (GET /auth/permissions
// falló, o el backend es anterior a los roles configurables): mismo criterio que
// pestanasPorDefecto en el backend — las cuentas Pre-SISI puras no entran al SISI, y una
// pestaña con `requiere` se ve solo si el rol tiene esas acciones. Así desplegar el
// frontend antes que el backend no deja a nadie sin pantallas.
const ROLES_SOLO_PRESISI = ['presisi'];

const pestanasPorDefecto = (rol: string, acciones: string[]): string[] => {
  const apps: App[] = ROLES_SOLO_PRESISI.includes(rol) ? ['app.presisi'] : ['app.sisi', 'app.presisi'];
  const pestanas = PESTANAS
    .filter(p => apps.includes(p.app))
    .filter(p => (p.requiere || []).every(r => acciones.includes(r)))
    .map(p => p.key);
  return [...apps, ...pestanas];
};

export const getFallbackPermissions = (role?: string | null): Permission[] =>
  fallbackByRole[normalizeRole(role)] || [];

export type UsuarioConPermisos = { rol?: string | null; permissions?: string[] | null } | null | undefined;

export const getUserPermissions = (user?: UsuarioConPermisos): string[] => {
  const base: string[] = Array.isArray(user?.permissions) ? user!.permissions! : getFallbackPermissions(user?.rol);
  if (base.some(p => p.startsWith('app.'))) return base;
  return [...base, ...pestanasPorDefecto(normalizeRole(user?.rol), base)];
};

export const hasPermission = (user: UsuarioConPermisos, permission: Clave) =>
  getUserPermissions(user).includes(permission);

export const tieneAcceso = (user: UsuarioConPermisos, app: App) => hasPermission(user, app);

// Primera pantalla que el usuario puede ver en esa app, en el orden del sidebar. Es el
// destino después del login y de cualquier guarda que rebote: antes era /dashboard fijo,
// pero ahora un rol puede no tener Dashboard. null = no tiene ninguna pestaña ahí.
export const primeraPestanaPermitida = (user: UsuarioConPermisos, app: App = 'app.sisi'): string | null => {
  if (!tieneAcceso(user, app)) return null;
  const permisos = getUserPermissions(user);
  return PESTANAS.find(p => p.app === app && permisos.includes(p.key))?.ruta || null;
};

export const isPrivilegedRole = (role?: string | null) =>
  hasPermission({ rol: role }, PERMISSIONS.ACCREDITATION_MANAGE);

export const isAdminRole = (role?: string | null) =>
  hasPermission({ rol: role }, PERMISSIONS.USERS_MANAGE);

// Acceso al módulo Programación/Planificación. A diferencia de isPrivilegedRole/isAdminRole,
// recibe el usuario completo para respetar el array `permissions` que entrega el backend en
// GET /auth/permissions (fuente de verdad); si no viene, cae al fallback por rol.
// Espejo de PROGRAMACION_ACCESS en zc_backend/middleware/permissions.js.
export const hasProgramacionAccess = (
  user: { rol?: string | null; permissions?: string[] | null } | null | undefined
) => hasPermission(user, PERMISSIONS.PROGRAMACION_ACCESS);

// Acceso de administrador dentro de la plataforma principal. Misma idea que
// isAdminRole (USERS_MANAGE es el permiso que sólo tiene admin), pero recibe el usuario
// completo para respetar el array `permissions` que entrega el backend en
// GET /auth/permissions; si no viene, cae al fallback por rol.
export const isAdminUser = (
  user: { rol?: string | null; permissions?: string[] | null } | null | undefined
) => hasPermission(user, PERMISSIONS.USERS_MANAGE);
